import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Call, Device } from '@twilio/voice-sdk';
import { Observable, Subject } from 'rxjs';

/** Host supplies this to fetch a fresh JWT (keeps the service decoupled from tmQueueService). */
export type TokenProvider = () => Promise<string>;

/** Host supplies this to fetch NTS STUN/TURN servers (already mapped to RTCIceServer[]). */
export type IceProvider = () => Promise<RTCIceServer[]>;

export type TwilioDeviceStatus =
  | 'idle' | 'registering' | 'registered' | 'unregistered' | 'destroyed' | 'error';

export interface QualityWarning {
  category: 'network' | 'audio';
  name: string;
  message: string;
  active: boolean;      // true = degraded, false = cleared
  data?: any;
}

export interface IncomingCallInfo {
  call: Call;
  from: string;
  conferenceId?: string;
  callSid?: string;
  isAutomated: boolean;
}
export interface AcceptCallRequest {
  call: Call;
  callData?: any;
  relayOnly?: boolean;
}
@Injectable({ providedIn: 'root' })
export class TwilioVoiceService implements OnDestroy {
  private device: Device | null = null;
  private activeCall: Call | null = null;
  private tokenProvider!: TokenProvider;

  /** Cached NTS ICE servers, fetched once at initialize() and injected per-call. */
  private iceServers?: RTCIceServer[];

  private tokenRefreshing = false;
  private tokenErrorCount = 0;
  private readonly MAX_TOKEN_RETRY = 3;

  private reRegisterTimer: any = null;
  private readonly RE_REGISTER_BACKOFF_MS = 3000;

  // Token / transient signaling error codes.
  private readonly TOKEN_CODES = [20101, 20104, 31204, 31205, 31207];
  private readonly TRANSIENT_CODES = [31000, 31003, 31005, 31009, 31107];

  // ---- Public event streams ----
  private readonly _status$        = new Subject<TwilioDeviceStatus>();
  private readonly _incoming$      = new Subject<IncomingCallInfo>();
  private readonly _accepted$      = new Subject<AcceptCallRequest>();
  private readonly _ended$         = new Subject<Call | null>();
  private readonly _quality$       = new Subject<QualityWarning>();
  private readonly _reconnecting$  = new Subject<any>();
  private readonly _reconnected$   = new Subject<void>();
  private readonly _error$         = new Subject<any>();

  readonly status$        = this._status$.asObservable();
  readonly incoming$      = this._incoming$.asObservable();
  readonly accepted$      = this._accepted$.asObservable();
  readonly ended$         = this._ended$.asObservable();
  readonly quality$       = this._quality$.asObservable();
  readonly reconnecting$  = this._reconnecting$.asObservable();
  readonly reconnected$   = this._reconnected$.asObservable();
  readonly error$         = this._error$.asObservable();

  private readonly rawAudioConstraints: MediaTrackConstraints = {
    noiseSuppression: { exact: false },
    echoCancellation:true,
    autoGainControl:true,
  };

  constructor(private zone: NgZone) {}

  // =========================================================
  //  DEVICE LIFECYCLE
  // =========================================================
  async initialize(token: string, tokenProvider: TokenProvider, iceProvider?: IceProvider): Promise<void> {
    this.tokenProvider = tokenProvider;

    // Always start clean -> prevents duplicate Device / listener leaks.
    await this.destroy();

    // Request mic permission early so the first call isn't blocked/delayed.
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      console.error('[TwilioVoice] Microphone permission error:', e);
    }

    // Fetch + cache NTS ICE servers.
// NOTE: The NTS endpoints (global.turn/stun.twilio.com) are anycast / Global Low Latency,
// so Twilio already routes each client to its NEAREST TURN/STUN region automatically.
// We intentionally do NOT rewrite 'global' to a fixed region here -> that would override
// Twilio's smart nearest-edge routing. Use the servers exactly as returned.
try {
  const fetched = iceProvider ? await iceProvider() : undefined;
  this.iceServers = fetched && fetched.length ? fetched : undefined;
} catch (e) {
  console.warn('[TwilioVoice] NTS fetch failed, using default GLL', e);
  this.iceServers = undefined;
}

    this._status$.next('registering');
    console.log('[TwilioVoice] Device setup initialized & token generated- Time', new Date().toLocaleTimeString());
    this.device = new Device(token, {
      codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
      edge: 'roaming',        // low-latency edge for Chennai, roaming fallback
      // maxAverageBitrate: 24000,              // cap Opus ~24 kbps for constrained (<100 kbps) links
      dscp: true,                            // QoS marking on media
      closeProtection: true,
      maxCallSignalingTimeoutMs: 30000,
      tokenRefreshMs: 30000,
      forceAggressiveIceNomination: true,    // faster ICE nomination on lossy nets
      enableImprovedSignalingErrorPrecision: true,
      allowIncomingWhileBusy: false,
      logLevel:'WARN',                     // switch to 'DEBUG' temporarily when diagnosing
    });

    this.attachDeviceListeners(this.device);
    this.applyAudioSettings();

    await this.device.register();
  }

  private attachDeviceListeners(device: Device): void {
    device.on('registering', () => this.zone.run(() => this._status$.next('registering')));

    device.on('registered', () => this.zone.run(() => {
      this.tokenErrorCount = 0;
      this.tokenRefreshing = false;
      this._status$.next('registered');
    }));

    device.on('unregistered', () => this.zone.run(() => {
      this._status$.next('unregistered');

      // Guard against the re-register storm seen on very slow (<10 kbps) links:
      // - skip if we're mid token-swap
      // - skip if the device is still 'registering' (avoids the InvalidStateError race)
      // - back off before retrying so a dead link doesn't hammer register()
      if (this.tokenRefreshing) return;
      if (!this.device || this.device.state === 'destroyed') return;
      if (this.device.state === 'registering') return;

      clearTimeout(this.reRegisterTimer);
      this.reRegisterTimer = setTimeout(() => {
        if (this.device && this.device.state === 'unregistered') {
          this.device.register().catch(err => console.error('[TwilioVoice] re-register failed', err));
        }
      }, this.RE_REGISTER_BACKOFF_MS);
    }));

    device.on('incoming', (call: Call) => this.zone.run(() => this.handleIncoming(call)));

    device.on('tokenWillExpire', () => this.refreshToken());

    //device.on('error', (error: any) => this.zone.run(() => {
    //  this._error$.next(error);
    //  this.handleTokenErrors(error);
      //}));

      device.on('error', (error: any) => {
          this.zone.run(() => {
              const code = this.getTwilioErrorCode(error);

              if (this.isTokenError(error)) {
                  console.warn(
                      `[TwilioVoice] Token error ${code}. Refreshing the access token.`
                  );

                  this.handleTokenError(error);
                  return;
              }

              if (this.isTransientError(error)) {
                  console.warn(
                      `[TwilioVoice] Transient signalling error ${code}. ` +
                      `Twilio SDK will handle reconnection.`
                  );

                  // Do not emit through error$, so it will not be sent to the server.
                  return;
              }

              // Emit only errors that require application/server logging.
              this._error$.next(error);
          });
      });

    // IMPORTANT: Device-level disconnect must NOT call disconnectAll() - per-call cleanup lives on the Call object.
    device.on('disconnect', () => this.zone.run(() => { /* intentionally no-op */ }));

    device.audio?.on('deviceChange', () => this.applyAudioSettings());
  }

  // =========================================================
  //  TOKEN MANAGEMENT  (async/arrow -> no `this` loss)
  // =========================================================
  private async refreshToken(): Promise<void> {
    if (this.tokenRefreshing || !this.device) return;
    this.tokenRefreshing = true;
    try {
      const fresh = await this.tokenProvider();
      if (this.device && this.device.state !== 'destroyed') {
        this.device.updateToken(fresh);
        console.log('[TwilioVoice] token was updated successfully - Time', new Date().toLocaleTimeString());
      } else {
        console.log('[TwilioVoice] token update skipped - device state:', this.device?.state);
      }
      this.tokenErrorCount = 0;
    } catch (err) {
      this.tokenErrorCount++;
      if (this.tokenErrorCount < this.MAX_TOKEN_RETRY) {
        this.tokenRefreshing = false;
        return this.refreshToken();
      }
      console.error('[TwilioVoice] token refresh failed after retries.', err);
    } finally {
      this.tokenRefreshing = false;
    }
  }

    private handleTokenError(error: any): void {
                
        this.tokenErrorCount = 0;
        this.tokenRefreshing = true;
        this.refreshToken();
    }

  private handleTokenErrors(error: any): void {
    if (this.TOKEN_CODES.includes(error?.code)) {
      this.tokenErrorCount = 0;
      this.tokenRefreshing = false;
      this.refreshToken();
      return;
    }
    // Transient signaling codes -> SDK auto-reconnects; do NOT drop the call.
    if (this.TRANSIENT_CODES.includes(error?.code)) {
      return;
    }
  }


    getTwilioErrorCode(error: any): number | null {
        const rawCode =
            error?.code ??
            error?.twilioError?.code ??
            error?.originalError?.code;

        if (rawCode === null || rawCode === undefined || rawCode === '') {
            return null;
        }

        const code = Number(rawCode);
        return Number.isFinite(code) ? code : null;
    }

    private isTokenError(error: any): boolean {
        const code = this.getTwilioErrorCode(error);
        return code !== null && this.TOKEN_CODES.includes(code);
    }

    private isTransientError(error: any): boolean {
        const code = this.getTwilioErrorCode(error);
        return code !== null && this.TRANSIENT_CODES.includes(code);
    }

  // =========================================================
  //  NTS / RTC CONFIG
  // =========================================================
  /**
   * Build the per-call RTCConfiguration.
   * @param relayOnly force TURN-only (media over turn:...:443?transport=tcp) for locked-down / VPN networks.
   */
  private buildRtcConfig(relayOnly = false): RTCConfiguration | undefined {
    if (!this.iceServers || !this.iceServers.length) {
      return relayOnly ? { iceServers: [], iceTransportPolicy: 'relay' } : undefined;
    }
    return {
      iceServers: this.iceServers,
      ...(relayOnly ? { iceTransportPolicy: 'relay' as RTCIceTransportPolicy } : {}),
    };
  }

  // =========================================================
  //  OUTBOUND NOT USE
  // =========================================================
  async makeCall(params: Record<string, string>, relayOnly = true): Promise<Call> {
    if (!this.device) throw new Error('Device not initialized');
    if (this.activeCall) this.hangup();          // guard against duplicate PeerConnections
    const call = await this.device.connect({
      params,
      rtcConfiguration: this.buildRtcConfig(relayOnly),   // NTS injected at the correct per-call location
      rtcConstraints: {
        audio: this.rawAudioConstraints,
      },
    });
    this.bindCall(call);
    this.activeCall = call;
    return call;
  }

  // =========================================================
  //  INBOUND
  // =========================================================
  private handleIncoming(call: Call): void {
    if (this.activeCall) { call.reject(); return; }  // reject 2nd incoming while busy
    this._incoming$.next({
      call,
      from: call.parameters['From'],
      conferenceId: call.customParameters.get('Conference-ID') || undefined,
      callSid: call.parameters['CallSid'],
      isAutomated: call.customParameters.get('Automated-Call') === 'true',
    });
  }

  acceptCall({call,callData,relayOnly = true}: AcceptCallRequest): void {  
    this.applyAudioSettings();
  
    this.bindCall(call, callData);
    this.activeCall = call;
  
    call.accept({
      rtcConfiguration: this.buildRtcConfig(relayOnly),
      rtcConstraints: {
        audio: this.rawAudioConstraints,
      },
    });
  }

  rejectCall(call: Call): void {
    call.reject();
  }

  // =========================================================
  //  PER-CALL LISTENERS (single source of truth for a Call)
  // =========================================================
  private bindCall(call: Call,callData?:any): void {
    call.on('accept', () => this.zone.run(() => this._accepted$.next({call, callData})));

    call.on('disconnect', () => this.zone.run(() => {
      if (this.activeCall === call) this.activeCall = null;
      this.unsetInputDevice();
      this._ended$.next(call);
    }));

    call.on('cancel', () => this.zone.run(() => {
      if (this.activeCall === call) this.activeCall = null;
      this._ended$.next(call);
    }));

    call.on('reject', () => this.zone.run(() => this._ended$.next(call)));

    call.on('error', (err: any) => this.zone.run(() => this._error$.next(err)));

    // Quality + reconnection: keep the call ALIVE, just inform the UI.
    call.on('warning', (name: string, data: any) =>
      this.zone.run(() => this._quality$.next(this.classifyWarning(name, data, true))));
    call.on('warning-cleared', (name: string) =>
      this.zone.run(() => this._quality$.next(this.classifyWarning(name, null, false))));

    call.on('reconnecting', (err: any) => this.zone.run(() => this._reconnecting$.next(err)));
    call.on('reconnected', () => this.zone.run(() => this._reconnected$.next()));
  }

  /** Correctly categorize warnings (fixes the always-true bug in the old CallWarning). */
  private classifyWarning(name: string, data: any, active: boolean): QualityWarning {
    const audioWarnings = ['constant-audio-input-level', 'constant-audio-output-level'];
    const category: 'network' | 'audio' = audioWarnings.includes(name) ? 'audio' : 'network';
    const message = category === 'audio'
      ? 'Audio device or driver issue detected.'
      : 'Slow or unstable network connection detected.';
    return { category, name, message, active, data };
  }

  // =========================================================
  //  IN-CALL CONTROLS
  // =========================================================
  mute(state: boolean): void { this.activeCall?.mute(state); }
  isMuted(): boolean { return !!this.activeCall?.isMuted(); }
  sendDigits(digit: string): void { this.activeCall?.sendDigits(digit); }

  /** End ONLY the active call - never disconnectAll here. */
  hangup(): void {
    this.activeCall?.disconnect();
    this.activeCall = null;
  }

  /** Only for full reset / logout / menu-navigation scenarios. */
  disconnectAll(): void {
    this.device?.disconnectAll();
    this.activeCall = null;
  }

  getActiveCall(): Call | null { return this.activeCall; }
  hasActiveCall(): boolean { return !!this.activeCall; }
  isRegistered(): boolean { return this.device?.state === 'registered'; }
  getDeviceState(): string { return this.device?.state ?? 'idle'; }

  // =========================================================
  //  AUDIO DEVICES
  // =========================================================
  async applyAudioSettings(): Promise<void> {
    const audio = this.device?.audio;
    if (!audio) return;
  
    const savedInput = localStorage.getItem('deviceInputSelection');
    const savedOutput = localStorage.getItem('deviceOutputSelection');
  
    const requestedInput =
      savedInput && savedInput !== 'undefined' ? savedInput : 'default';
  
    const requestedOutput =
      savedOutput && savedOutput !== 'undefined' ? savedOutput : 'default';
  
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      
      const inputId = devices.some(
        device =>
          device.kind === 'audioinput' &&
          device.deviceId === requestedInput
      )
        ? requestedInput
        : 'default';
  
      const outputId = devices.some(
        device =>
          device.kind === 'audiooutput' &&
          device.deviceId === requestedOutput
      )
        ? requestedOutput
        : 'default';
      // Apply before acquiring the microphone.
      await audio.setAudioConstraints(this.rawAudioConstraints);
      await audio.setInputDevice(inputId);   
      if (audio.isOutputSelectionSupported) {
        const results = await Promise.allSettled([
          audio.speakerDevices.set(outputId),
          audio.ringtoneDevices.set(outputId),
        ]);
  
        results.forEach(result => {
          if (result.status === 'rejected') {
            console.warn(
              '[TwilioVoice] Output-device selection failed:',
              result.reason
            );
          }
        });
      }
  
      // Optional verification
      // const settings = audio.inputStream
      //   ?.getAudioTracks()[0]
      //   ?.getSettings();
  
      //console.debug('[TwilioVoice] Active audio settings:', settings);
    } catch (error) {
      console.error(
        '[TwilioVoice] applyAudioSettings error:',
        error
      );
    }
  }

  async unsetInputDevice(): Promise<void> {
    try { await this.device?.audio?.unsetInputDevice(); } catch { /* noop */ }
  }

  // =========================================================
  //  TEARDOWN
  //  Fixes the <10 kbps InvalidStateError:
  //  only unregister() from 'registered', and catch the async rejection.
  // =========================================================
  async destroy(): Promise<void> {
    clearTimeout(this.reRegisterTimer);

    const d = this.device;
    this.device = null;          // null out first so re-entrancy / re-register guards are safe
    this.activeCall = null;

    if (d) {
      try { d.disconnectAll(); } catch { /* noop */ }

      // unregister() is legal ONLY from the 'registered' state and is async.
      // On slow networks the device is often stuck in 'registering' -> calling
      // unregister() there throws InvalidStateError. Guard + await/catch it.
      try {
        if (d.state === 'registered') {
          await d.unregister();
        }
      } catch (e) {
        // Safe to ignore: state race on constrained networks.
        console.debug('[TwilioVoice] unregister skipped/failed (non-fatal):', e);
      }

      try { d.destroy(); } catch { /* noop */ }
    }

    this._status$.next('destroyed');
  }

  ngOnDestroy(): void { this.destroy(); }
   
}
 